// OfflineBanner — global indicator for connectivity + sync state.
//
// Renders nothing when we're online and the mutation queue is empty.
// When offline, shows a thin banner at the top with the pending count.
// When back online with a non-zero queue, shows "Syncing X changes…" until
// the sync engine drains.
//
// We deliberately keep this presentational and read-only — actual sync is
// kicked off by the syncEngine wired in App.js.

import { useEffect, useState } from "react";
import { listQueue } from "@/lib/mutationQueue";
import { getMeta } from "@/lib/offlineCache";

const POLL_MS = 4000;

const formatRelative = (ts) => {
  if (!ts) return null;
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  const mins = Math.round(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

export const OfflineBanner = () => {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [lastSynced, setLastSynced] = useState(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const q = await listQueue();
        const ls = await getMeta("last_synced_at");
        if (!alive) return;
        setQueueCount(q.length);
        setLastSynced(ls || null);
        // iOS PWA hardening: Safari (especially when installed to home
        // screen) sometimes drops `online`/`offline` events. Read the
        // current navigator.onLine on every poll so the banner stays in
        // sync even when events don't fire.
        if (typeof navigator !== "undefined" && navigator.onLine !== online) {
          setOnline(navigator.onLine);
        }
      } catch {}
    };
    refresh();
    const handle = setInterval(refresh, POLL_MS);
    return () => {
      alive = false;
      clearInterval(handle);
    };
  }, [online]);

  // Nothing to show: online, no pending changes.
  if (online && queueCount === 0) return null;

  const offlineColor = "rgb(120, 53, 15)";    // amber-900
  const offlineBg = "rgb(254, 243, 199)";     // amber-100
  const syncColor = "rgb(30, 64, 175)";       // blue-800
  const syncBg = "rgb(219, 234, 254)";        // blue-100

  const isSyncing = online && queueCount > 0;
  const bg = isSyncing ? syncBg : offlineBg;
  const fg = isSyncing ? syncColor : offlineColor;

  let label;
  if (!online) {
    label = queueCount > 0
      ? `Offline — ${queueCount} change${queueCount === 1 ? "" : "s"} will sync when you reconnect`
      : "Offline — viewing cached data";
  } else {
    label = `Syncing ${queueCount} change${queueCount === 1 ? "" : "s"}…`;
  }

  const lastSyncedLabel = lastSynced ? `Last synced ${formatRelative(lastSynced)}` : null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        width: "100%",
        background: bg,
        color: fg,
        padding: "6px 12px",
        fontSize: 13,
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        borderBottom: `1px solid ${fg}33`,
      }}
    >
      <span>{label}</span>
      {lastSyncedLabel && (
        <span style={{ opacity: 0.7, fontWeight: 400 }}>· {lastSyncedLabel}</span>
      )}
    </div>
  );
};

export default OfflineBanner;
